import { BsdsInput } from '@bsds-lib-component/react';
import React, { useId, type MutableRefObject } from 'react';

import { CpfInput, Dropdown, Visibility } from '../..';
import { usePersonFormSection } from '../../../hooks/vida/usePersonFormSection';
import type { NewInsuredFormValues } from '../../../types/vida/cadastralMaintenance/newInsuredFormTypes';
import type { PersonFormSectionLabels, PersonFormSection, PersonFormSectionControl } from '../../../types/vida/cadastralMaintenance/personFormTypes';
import {
    getFormValuesFromInitial,
    isInsuredFormValid,
    MATRICULA_MAX_LENGTH,
    NUMERO_MAX_LENGTH,
    PERSON_NAME_MAX_LENGTH,
} from '../../../utils/insuredFormUtils';
import { PROPONENT_SEX_OPTIONS } from '../../../utils/proponentFormUtils';
import {
    PersonFormAddressFields,
    PersonFormDateInput,
    PersonFormInsuranceFields,
    PersonFormSectionNav,
} from './PersonFormSharedFields';

type SharedInsuredFormSectionLabels = PersonFormSectionLabels;

type SharedInsuredFormSectionProps = {
    labels: SharedInsuredFormSectionLabels;
    onValidityChange?: (isValid: boolean) => void;
    onValuesChange?: (values: NewInsuredFormValues) => void;
    initialValues?: NewInsuredFormValues;
    readOnly?: boolean;
    showDataCadastro?: boolean;
    cpfReadOnly?: boolean;
    dataCadastroReadOnly?: boolean;
    extraNavButtons?: React.ReactNode;
    hideFormSections?: boolean;
    hideNavButtons?: boolean;
    onSectionNavChange?: (section: PersonFormSection) => void;
    sectionControlRef?: MutableRefObject<PersonFormSectionControl | null>;
    onActiveSectionChange?: (section: PersonFormSection) => void;
};

const SharedInsuredFormSection: React.FC<SharedInsuredFormSectionProps> = ({
    labels,
    onValidityChange,
    onValuesChange,
    initialValues,
    readOnly = false,
    showDataCadastro = false,
    cpfReadOnly = false,
    dataCadastroReadOnly = false,
    extraNavButtons,
    hideFormSections = false,
    hideNavButtons = false,
    onSectionNavChange,
    sectionControlRef,
    onActiveSectionChange,
}) => {
    const sexoDropdownId = useId();
    const ufDropdownId = useId();
    const {
        values,
        activeSection,
        setActiveSection,
        setIsCpfValid,
        handleInputChange,
        handleCepChange,
        handleCpfChange,
        handleDropdownChange,
        handleZipCodeSearch,
        isZipCodeLookupLoading,
        zipCodeLookupError,
        inputDisabled,
    } = usePersonFormSection({
        initialValues,
        getFormValuesFromInitial,
        isBaseFormValid: isInsuredFormValid,
        readOnly,
        onValidityChange,
        onValuesChange,
        sectionControlRef,
        onActiveSectionChange,
    });

    const handleSectionChange = (section: PersonFormSection) => {
        setActiveSection(section);
        onSectionNavChange?.(section);
    };

    return (
        <>
            {!hideNavButtons && (
                <PersonFormSectionNav
                    labels={labels}
                    activeSection={activeSection}
                    onSectionChange={handleSectionChange}
                    extraNavButtons={extraNavButtons}
                    highlightActiveSection={!hideFormSections}
                />
            )}

            {!hideFormSections && (
                <>
                    <Visibility isActive={activeSection === 'personData'} animated animationDuration={300}>
                        <div>
                            <div className="bsds-mt-16">
                                <div className="grid grid-cols-1 gap-4">
                                    <BsdsInput
                                        label="Nome Completo *"
                                        value={values.nome}
                                        onInput={handleInputChange('nome')}
                                        type="text"
                                        clearInput={false}
                                        platform="web"
                                        required
                                        disabled={inputDisabled}
                                        maxlength={PERSON_NAME_MAX_LENGTH}
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
                                    <CpfInput
                                        label="CPF *"
                                        value={values.cpf}
                                        onChange={handleCpfChange}
                                        onValidationChange={setIsCpfValid}
                                        required
                                        disabled={inputDisabled || cpfReadOnly}
                                    />
                                    <BsdsInput
                                        label="Matrícula *"
                                        value={values.matricula}
                                        onInput={handleInputChange('matricula')}
                                        type="text"
                                        platform="web"
                                        required
                                        disabled={inputDisabled}
                                        clearInput={false}
                                        maxlength={MATRICULA_MAX_LENGTH}
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
                                    <Dropdown
                                        id={sexoDropdownId}
                                        label="Sexo *"
                                        placeholder="Selecione o sexo"
                                        value={values.sexo}
                                        onChange={handleDropdownChange('sexo')}
                                        options={PROPONENT_SEX_OPTIONS}
                                        required
                                        disabled={inputDisabled}
                                    />
                                    <PersonFormDateInput
                                        label="Data de Nascimento *"
                                        value={values.dataNascimento}
                                        onInput={handleInputChange('dataNascimento')}
                                        disabled={inputDisabled}
                                        ariaLabel="Data de nascimento (obrigatório)"
                                    />
                                </div>
                                {showDataCadastro && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mt-16">
                                        <PersonFormDateInput
                                            label="Data de Cadastro *"
                                            value={values.dataCadastro}
                                            onInput={handleInputChange('dataCadastro')}
                                            disabled={inputDisabled || dataCadastroReadOnly}
                                            ariaLabel="Data de cadastro (obrigatório)"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </Visibility>

                    <PersonFormAddressFields
                        isActive={activeSection === 'personAddress'}
                        values={values}
                        ufDropdownId={ufDropdownId}
                        inputDisabled={inputDisabled}
                        onInputChange={handleInputChange}
                        onCepChange={handleCepChange}
                        onDropdownChange={handleDropdownChange}
                        onZipCodeSearch={() => {
                            void handleZipCodeSearch();
                        }}
                        isZipCodeLookupLoading={isZipCodeLookupLoading}
                        zipCodeLookupError={zipCodeLookupError}
                        extraFields={(
                            <BsdsInput
                                label="Número *"
                                value={values.numero}
                                onInput={handleInputChange('numero')}
                                type="text"
                                inputmode="numeric"
                                platform="web"
                                required
                                disabled={inputDisabled}
                                clearInput={false}
                                maxlength={NUMERO_MAX_LENGTH}
                                aria-label="Número do endereço (obrigatório)"
                                aria-required
                            />
                        )}
                    />

                    <PersonFormInsuranceFields
                        isActive={activeSection === 'insuranceInfo'}
                        values={values}
                        inputDisabled={inputDisabled}
                        onInputChange={handleInputChange}
                    />
                </>
            )}
        </>
    );
};

export type { SharedInsuredFormSectionLabels, SharedInsuredFormSectionProps };
export default SharedInsuredFormSection;
